import {
  Button,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Tab,
  TabList,
  Tooltip,
  Toast,
  ToastTitle,
} from '@fluentui/react-components';
import {
  Delete20Filled,
  Delete20Regular,
  Dismiss20Filled,
  Dismiss20Regular,
  Edit20Filled,
  Edit20Regular,
  History20Filled,
  History20Regular,
  MoreHorizontal20Filled,
  MoreHorizontal20Regular,
  Play20Filled,
  Play20Regular,
  Save20Filled,
  Save20Regular,
  bundleIcon,
} from '@fluentui/react-icons';
import { useEffect, useMemo, useState } from 'react';

import { useHttpClient } from '../../../api/HttpClientContext.js';
import { VersionsRail } from '../../../components/VersionsRail/VersionsRail';
import { VersionStatusBadge } from '../../../components/VersionStatusBadge/index.js';
import { useGraderActions } from '../../../context/GraderContext';
import type { Grader } from '../../../hooks/useGraders/useGraders.interface';
import { useGraderVersions } from '../../../hooks/useGraderVersions/useGraderVersions';
import type { GraderVersion } from '../../../services/grader.interface';
import { WorkflowApiService } from '../../../services/workflowApi.js';
import { useUserBi } from '../../../telemetry/hooks/useUserBi/useUserBi.js';
import {
  EntityType,
  OperationType,
  PanelType,
} from '../../../telemetry/userbi.js';
import { useStyles } from '../GraderPage.style';
import { graderTypeSupportsEvaluate } from '../graderTypes/graderTypeRegistry.js';
import { variableRowsFromMap } from '../graderVariables.utils.js';

import {
  GraderEvaluateTab,
  type GraderEvaluateControls,
} from './GraderEvaluateTab/GraderEvaluateTab';
import { GraderVersionSnapshotDialog } from './GraderVersionSnapshotDialog';
import { OverviewTab } from './OverviewTab';
import { useEditGrader } from './useEditGrader';

/* eslint-disable @typescript-eslint/no-unsafe-assignment */
const PlayIcon = bundleIcon(Play20Filled, Play20Regular);
const EditIcon = bundleIcon(Edit20Filled, Edit20Regular);
const DeleteIcon = bundleIcon(Delete20Filled, Delete20Regular);
const SaveIcon = bundleIcon(Save20Filled, Save20Regular);
const DismissIcon = bundleIcon(Dismiss20Filled, Dismiss20Regular);
const HistoryIcon = bundleIcon(History20Filled, History20Regular);
const MoreIcon = bundleIcon(MoreHorizontal20Filled, MoreHorizontal20Regular);
/* eslint-enable @typescript-eslint/no-unsafe-assignment */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type DetailViewTab = 'overview' | 'evaluate';

export interface GraderDetailViewProps {
  judge: Grader;
  activeTab: DetailViewTab;
  onTabChange: (tab: DetailViewTab) => void;
  /** Workspace, used to deep-link to the source judge in the Judge Factory. */
  workspaceName: string;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function GraderDetailView({
  judge,
  activeTab,
  onTabChange,
  workspaceName,
}: GraderDetailViewProps) {
  const styles = useStyles();
  const client = useHttpClient();
  const service = useMemo(() => new WorkflowApiService(client), [client]);
  const { trackOperation } = useUserBi();
  const { openDeleteDialog, patchGrader, dispatchToast } = useGraderActions();

  // The Evaluate tab reports its run-button state up so the action can live in
  // this header (mirrors the Eval Template page's "Queue job" placement).
  const [evalControls, setEvalControls] =
    useState<GraderEvaluateControls | null>(null);

  const edit = useEditGrader(judge, workspaceName);

  // Version history is fetched for the title badge; the rail stays collapsed
  // until requested and resets when the selected grader changes.
  const [showVersions, setShowVersions] = useState(false);
  // The snapshot version whose read-only preview dialog is open (null = closed).
  const [viewedVersion, setViewedVersion] = useState<GraderVersion | null>(
    null,
  );
  const [snapshotting, setSnapshotting] = useState(false);
  const [snapshotVersion, setSnapshotVersion] = useState<number | null>(null);
  useEffect(() => {
    setShowVersions(false);
    setViewedVersion(null);
    setSnapshotVersion(null);
  }, [judge.name]);

  const {
    versions,
    isLoading: versionsLoading,
    error: versionsError,
    refresh: refreshVersions,
  } = useGraderVersions({
    workspaceName,
    // Version metadata powers the title badge even while the rail is hidden.
    graderName: judge.name,
  });

  // The Evaluate flow only supports grader types that run against a live model
  // (it wraps the grader as an agent-under-test and rewrites its LLM prompt).
  // Hide the tab for every other grader type, and never render its panel — even
  // if `activeTab` was left on 'evaluate' by a previously selected LLM judge.
  const canEvaluate = graderTypeSupportsEvaluate(judge.graderType);
  const effectiveTab: DetailViewTab =
    activeTab === 'evaluate' && !canEvaluate ? 'overview' : activeTab;
  const editableTab = effectiveTab === 'overview';
  const persistedVariables = useMemo(
    () => variableRowsFromMap(judge.variables),
    [judge.variables],
  );
  const historyVersion = versions[versions.length - 1]?.version;
  const latestVersion =
    snapshotVersion === null
      ? historyVersion
      : Math.max(snapshotVersion, historyVersion ?? 0);

  const handleSnapshot = async () => {
    if (snapshotting) return;
    setSnapshotting(true);
    try {
      const snapshot = await trackOperation(
        {
          panelType: PanelType.GradersPage,
          entityType: EntityType.Grader,
          operation: OperationType.Snapshot,
        },
        () => service.snapshotGrader(workspaceName, judge.name),
      );
      patchGrader(judge.id, {
        isDraft: false,
      });
      setSnapshotVersion(snapshot.version);
      refreshVersions();
      dispatchToast(
        <Toast>
          <ToastTitle>Snapshot v{snapshot.version} created</ToastTitle>
        </Toast>,
        { intent: 'success', timeout: 5000 },
      );
    } catch {
      dispatchToast(
        <Toast>
          <ToastTitle>Failed to snapshot grader</ToastTitle>
        </Toast>,
        { intent: 'error', timeout: 5000 },
      );
    } finally {
      setSnapshotting(false);
    }
  };

  return (
    <>
      <div className={styles.detailEditorHeader}>
        <div className={styles.editorTitleRow}>
          <span className={styles.editorName}>
            {judge.displayName?.trim() ? judge.displayName : judge.name}
          </span>
          <VersionStatusBadge
            isDraft={judge.isDraft === true}
            version={latestVersion}
          />
        </div>
        <div className={styles.editorActions}>
          {effectiveTab === 'evaluate' && (
            <Button
              appearance="primary"
              icon={<PlayIcon />}
              onClick={() => evalControls?.run()}
              disabled={
                evalControls === null ||
                !evalControls.canRun ||
                evalControls.running
              }
            >
              {evalControls?.running ? 'Starting…' : 'Run evaluation'}
            </Button>
          )}
          {editableTab && edit.canEdit && !edit.isEditing && (
            <>
              <Button icon={<EditIcon />} onClick={edit.start}>
                Edit
              </Button>
              {judge.isDraft === true && (
                <Button
                  icon={<SaveIcon />}
                  onClick={() => void handleSnapshot()}
                  disabled={snapshotting}
                >
                  {snapshotting
                    ? 'Snapshotting…'
                    : latestVersion === undefined
                      ? 'Create snapshot'
                      : `Snapshot as v${String(latestVersion + 1)}`}
                </Button>
              )}
              <Button
                icon={<DeleteIcon />}
                onClick={() => openDeleteDialog(judge)}
              >
                Delete
              </Button>
            </>
          )}
          {editableTab && !edit.isEditing && (
            <Menu>
              <MenuTrigger disableButtonEnhancement>
                <Tooltip content="More actions" relationship="label">
                  <MenuButton icon={<MoreIcon />} aria-label="More actions" />
                </Tooltip>
              </MenuTrigger>
              <MenuPopover>
                <MenuList>
                  <MenuItem
                    icon={<HistoryIcon />}
                    onClick={() => setShowVersions((prev) => !prev)}
                  >
                    {showVersions ? 'Hide version history' : 'Version history'}
                  </MenuItem>
                </MenuList>
              </MenuPopover>
            </Menu>
          )}
          {editableTab && edit.isEditing && (
            <>
              <Button
                appearance="subtle"
                icon={<DismissIcon />}
                onClick={edit.cancel}
                disabled={edit.saving}
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                icon={<SaveIcon />}
                onClick={edit.save}
                disabled={
                  !edit.dirty ||
                  edit.saving ||
                  Object.keys(edit.variableErrors).length > 0 ||
                  edit.undeclaredVars.length > 0
                }
              >
                {edit.saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          )}
        </div>
      </div>

      <div className={styles.editorTabs}>
        <TabList
          selectedValue={effectiveTab}
          onTabSelect={(_, d) => onTabChange(d.value as DetailViewTab)}
          size="medium"
        >
          <Tab value="overview" className={styles.editorFirstTab}>
            Overview
          </Tab>
          {canEvaluate && <Tab value="evaluate">Evaluate</Tab>}
        </TabList>
      </div>

      {effectiveTab === 'evaluate' ? (
        <GraderEvaluateTab
          key={judge.name}
          workspaceName={workspaceName}
          graderName={judge.name}
          onControlsChange={setEvalControls}
        />
      ) : (
        <div className={styles.editorBodyRow}>
          <div className={styles.editorBody}>
            <OverviewTab
              judge={judge}
              workspaceName={workspaceName}
              isEditing={edit.isEditing}
              disabled={edit.saving}
              displayName={
                edit.isEditing ? edit.displayName : (judge.displayName ?? '')
              }
              onDisplayNameChange={edit.setDisplayName}
              description={
                edit.isEditing ? edit.description : judge.description
              }
              onDescriptionChange={edit.setDescription}
              modelName={edit.isEditing ? edit.modelName : (judge.model ?? '')}
              onModelNameChange={edit.setModelName}
              systemPrompt={
                edit.isEditing ? edit.systemPrompt : (judge.systemPrompt ?? '')
              }
              onSystemPromptChange={edit.setSystemPrompt}
              temperature={
                edit.isEditing ? edit.temperature : (judge.temperature ?? '')
              }
              onTemperatureChange={edit.setTemperature}
              topP={edit.isEditing ? edit.topP : (judge.topP ?? '')}
              onTopPChange={edit.setTopP}
              maxTokens={
                edit.isEditing ? edit.maxTokens : (judge.maxTokens ?? '')
              }
              onMaxTokensChange={edit.setMaxTokens}
              userMessage={
                edit.isEditing ? edit.userTemplate : (judge.userTemplate ?? '')
              }
              onUserMessageChange={edit.setUserTemplate}
              variables={edit.isEditing ? edit.variables : persistedVariables}
              variableErrors={edit.variableErrors}
              onVariableUpdate={edit.updateVariable}
              onVariableAdd={edit.addVariable}
              onVariableRemove={edit.removeVariable}
              undeclaredVars={edit.undeclaredVars}
              bareTokens={edit.bareTokens}
              templateFieldLabels={edit.templateFieldLabels}
              onDeclareVariable={edit.declareVariable}
              onConvertBareToken={edit.convertBareToken}
            />
          </div>

          {showVersions && (
            <VersionsRail
              versions={versions}
              loading={versionsLoading}
              error={versionsError}
              onHide={() => setShowVersions(false)}
              onViewVersion={setViewedVersion}
            />
          )}
        </div>
      )}

      <GraderVersionSnapshotDialog
        version={viewedVersion}
        workspaceName={workspaceName}
        onClose={() => setViewedVersion(null)}
      />
    </>
  );
}
